import { tutorialTaskDone } from '../../tutorial/events'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Screen } from '../components/Screen'
import { Field, useLeaveGuard } from '../components/Form'
import { EmptyState, ErrorState, SaveFailedAlert, Sheet, Skeleton } from '../components/States'
import { LimitNote, limitReason } from '../components/LimitNote'
import { useEnv } from '../context'
import { useClubContext, useCoaches, useGroup, useGroupAthletes, useInvalidateGroups, useSingleFlight } from '../data/hooks'
import { WEEK, dayName, formatSlotTime, minutesOf, sortSlots } from '../data/scheduleEdit'
import { SliceApiError, type CoachRef, type GroupDetail, type ScheduleSlot } from '../data/types'
import { useBackTo } from './AthleteScreen'

/**
 * Группы целиком в новом интерфейсе (этап 5 шаг 1, SC13): создание вне
 * мастера, правка названия, описания и тренеров, удаление с честным
 * предпросмотром. Расписание правится своим экраном (этап 3, шаги 8 и 10).
 */

// ---------------------------------------------------------------------------
// Дни и время: общие для мастера первого запуска и новой группы
// ---------------------------------------------------------------------------

/** Расписание новой группы: одни и те же часы в выбранные дни недели. */
export interface QuickSchedule {
  /** 0 = воскресенье, как в groups.schedule */
  days: number[]
  start: string
  end: string
}

export const EMPTY_QUICK_SCHEDULE: QuickSchedule = { days: [], start: '17:00', end: '18:00' }

/** Слоты для сервера; дни без выбора - пустое расписание. */
export function quickSlots(q: QuickSchedule): ScheduleSlot[] {
  return sortSlots(q.days.map((d) => ({ day_of_week: d, start_time: q.start, end_time: q.end })))
}

/** Ошибка времени; без выбранных дней время не проверяется. */
export function quickScheduleError(q: QuickSchedule): string | null {
  if (q.days.length === 0) return null
  const s = minutesOf(q.start)
  const e = minutesOf(q.end)
  if (s === null || e === null) return 'Укажите начало и конец занятия'
  return e <= s ? 'Конец занятия должен быть позже начала' : null
}

/** Дни занятий и одно время на все дни (мастер первого запуска, новая группа). */
export function QuickScheduleFields({
  value,
  onChange,
  hint = 'Можно не выбирать и добавить расписание позже',
}: {
  value: QuickSchedule
  onChange: (next: QuickSchedule) => void
  hint?: string
}) {
  const error = quickScheduleError(value)
  return (
    <>
      <div className="pc-field">
        <span className="pc-field__label" id="pc-days-label">
          Дни занятий
        </span>
        <div className="pc-chips pc-chips--touch" role="group" aria-labelledby="pc-days-label">
          {WEEK.map((w) => (
            <button
              key={w.day}
              type="button"
              aria-pressed={value.days.includes(w.day)}
              onClick={() =>
                onChange({
                  ...value,
                  days: value.days.includes(w.day) ? value.days.filter((x) => x !== w.day) : [...value.days, w.day],
                })
              }
            >
              {w.short}
            </button>
          ))}
        </div>
        <span className="pc-field__hint">{hint}</span>
      </div>
      {value.days.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'start' }}>
          <Field label="Начало">
            {(p) => (
              <input {...p} className="pc-input" type="time" value={value.start} onChange={(e) => onChange({ ...value, start: e.target.value })} />
            )}
          </Field>
          <Field label="Конец" error={error ?? undefined}>
            {(p) => <input {...p} className="pc-input" type="time" value={value.end} onChange={(e) => onChange({ ...value, end: e.target.value })} />}
          </Field>
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Тренеры группы: выбор из справочника и минимальное «Добавить тренера»
// ---------------------------------------------------------------------------

/**
 * Тренеры группы флажками. Справочник тренеров - отдельный раздел (этап 5
 * шаг 4); здесь только добавить нового, если нужного нет.
 */
function CoachPicker({ selected, onChange }: { selected: string[]; onChange: (ids: string[]) => void }) {
  const { api } = useEnv()
  const coaches = useCoaches()
  const client = useQueryClient()
  const flight = useSingleFlight()
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [error, setError] = useState<null | 'limit' | 'locked' | 'failed'>(null)

  const add = async () => {
    if (!name.trim()) return
    setError(null)
    try {
      const created = await flight.run(() => api.createCoach(name.trim()))
      if (!created) return
      client.setQueryData<CoachRef[]>(['redesign', 'coaches'], (list) =>
        [...(list ?? []), created].sort((a, b) => a.name.localeCompare(b.name, 'ru')),
      )
      void client.invalidateQueries({ queryKey: ['redesign', 'coaches'] })
      onChange([...selected, created.id])
      setName('')
      setAdding(false)
    } catch (e) {
      setError(limitReason(e))
    }
  }

  return (
    <section className="pc-section" aria-labelledby="pc-group-coaches">
      <h2 id="pc-group-coaches" className="pc-section-title">
        Тренеры группы
      </h2>
      {coaches.isPending ? (
        <Skeleton rows={1} label="Загружаю тренеров" />
      ) : coaches.isError ? (
        <ErrorState title="Не удалось загрузить тренеров" onRetry={() => void coaches.refetch()} />
      ) : coaches.data.length === 0 ? (
        <p className="pc-muted" style={{ margin: '0 0 8px', fontSize: 14 }}>
          В направлении пока нет тренеров. Добавьте, чтобы назначить на группу.
        </p>
      ) : (
        <ul className="pc-list pc-card pc-group-pick" aria-label="Тренеры направления">
          {coaches.data.map((c) => (
            <li key={c.id}>
              <label className="pc-check-row">
                <input
                  type="checkbox"
                  checked={selected.includes(c.id)}
                  onChange={(e) => onChange(e.target.checked ? [...selected, c.id] : selected.filter((x) => x !== c.id))}
                />
                <span className="pc-list-row__title">{c.name}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
      <p className="pc-muted" style={{ margin: '8px 0 0', fontSize: 13 }}>
        Тренер здесь - запись справочника для расписания и Штаба. Доступ в приложение он не получает: для этого - приглашение
        в «Команде и доступах».
      </p>
      {!adding ? (
        <button type="button" className="pc-btn pc-btn--block" style={{ marginTop: 8 }} onClick={() => setAdding(true)}>
          Добавить тренера
        </button>
      ) : (
        <div className="pc-card" style={{ marginTop: 8 }}>
          <Field label="Имя тренера">
            {(p) => (
              <input
                {...p}
                className="pc-input"
                value={name}
                maxLength={200}
                autoComplete="off"
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    void add()
                  }
                }}
              />
            )}
          </Field>
          {error === 'failed' && <SaveFailedAlert>Тренер не добавлен, попробуйте ещё раз.</SaveFailedAlert>}
          {(error === 'limit' || error === 'locked') && <LimitNote what="тренеров" reason={error} />}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              type="button"
              className="pc-btn"
              style={{ flex: 1 }}
              onClick={() => {
                setAdding(false)
                setName('')
                setError(null)
              }}
            >
              Отмена
            </button>
            <button type="button" className="pc-btn pc-btn--primary" style={{ flex: 2 }} disabled={!name.trim() || flight.busy} onClick={() => void add()}>
              {flight.busy ? 'Добавляю…' : 'Добавить'}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

/**
 * Назначить и снять тренеров по разнице «было -> стало». Каждый вызов
 * безопасно повторить: сервер назначенного второй раз не назначает.
 */
async function applyCoachDiff(
  setGroupCoach: (groupId: string, coachId: string, assigned: boolean) => Promise<void>,
  groupId: string,
  before: string[],
  after: string[],
): Promise<number> {
  let failed = 0
  const ops: [string, boolean][] = [
    ...after.filter((id) => !before.includes(id)).map((id): [string, boolean] => [id, true]),
    ...before.filter((id) => !after.includes(id)).map((id): [string, boolean] => [id, false]),
  ]
  for (const [id, on] of ops) {
    try {
      await setGroupCoach(groupId, id, on)
    } catch {
      failed += 1
    }
  }
  return failed
}

// ---------------------------------------------------------------------------
// Новая группа (/groups/new)
// ---------------------------------------------------------------------------

export function GroupNewScreen() {
  const navigate = useNavigate()
  const { api, toast } = useEnv()
  const club = useClubContext()
  const invalidate = useInvalidateGroups()
  const flight = useSingleFlight()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [quick, setQuick] = useState<QuickSchedule>(EMPTY_QUICK_SCHEDULE)
  const [coachIds, setCoachIds] = useState<string[]>([])
  const [nameError, setNameError] = useState<string>()
  const [error, setError] = useState<null | 'limit' | 'locked' | 'failed'>(null)
  const done = useRef(false)

  // Отказ виден, даже если кнопку нажали внизу длинной формы.
  const errorRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [error])

  const dirty = !!(name.trim() || description.trim() || quick.days.length || coachIds.length)
  const leave = useLeaveGuard(dirty)
  const backTo = useBackTo('/groups')
  const back = () => leave.guard(backTo)
  const timeError = quickScheduleError(quick)

  const save = async () => {
    if (!name.trim()) {
      setNameError('Укажите название группы')
      return
    }
    setNameError(undefined)
    if (timeError || done.current) return
    setError(null)
    try {
      await flight.run(async () => {
        const { id } = await api.createGroup(name.trim(), quickSlots(quick), description.trim() || undefined)
        tutorialTaskDone('group', id)
        if (quickSlots(quick).length) tutorialTaskDone('schedule')
        // Группа уже есть: повторное нажатие или сбой тренеров её не дублируют.
        done.current = true
        const coachFailed = await applyCoachDiff((gid, cid, on) => api.setGroupCoach(gid, cid, on), id, [], coachIds)
        await invalidate()
        toast(coachFailed > 0 ? 'Группа создана. Тренера назначить не удалось: назначьте в правке группы' : 'Группа создана')
        navigate(`/groups/${id}`, { replace: true })
      })
    } catch (e) {
      setError(limitReason(e))
    }
  }

  return (
    <Screen
      title="Новая группа"
      subtitle={club.data?.directionName ? `Направление «${club.data.directionName}»` : undefined}
      back={back}
      hideHub
      hideNav
      actionBar={
        <>
          <button type="button" className="pc-btn" style={{ flex: 1 }} onClick={back}>
            Отмена
          </button>
          <button
            type="button"
            className="pc-btn pc-btn--primary"
            style={{ flex: 2 }}
            disabled={flight.busy || !!timeError}
            onClick={() => void save()}
          >
            {flight.busy ? 'Создаю…' : 'Создать группу'}
          </button>
        </>
      }
    >
      <form
        className="pc-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        <div ref={errorRef}>
          {error === 'failed' && <SaveFailedAlert>Группа не создана, данные остались на экране. Попробуйте ещё раз.</SaveFailedAlert>}
          {(error === 'limit' || error === 'locked') && <LimitNote what="групп" reason={error} />}
        </div>
        <Field label="Название группы" hint="Например: «Стрижи» или «Младшая группа»" error={nameError}>
          {(p) => <input {...p} className="pc-input" value={name} maxLength={200} autoComplete="off" onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="Описание" hint="Можно не заполнять: возраст, уровень, зал">
          {(p) => <textarea {...p} className="pc-input" value={description} onChange={(e) => setDescription(e.target.value)} />}
        </Field>
        <QuickScheduleFields
          value={quick}
          onChange={setQuick}
          hint="Можно не выбирать. Разное время по дням и зал - после создания, в расписании группы"
        />
      </form>
      <CoachPicker selected={coachIds} onChange={setCoachIds} />
      {leave.dialog}
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Правка группы (/groups/:id/edit)
// ---------------------------------------------------------------------------

export function GroupEditScreen() {
  const { id = '' } = useParams()
  const group = useGroup(id)
  const back = useBackTo(`/groups/${id}`)

  if (group.isPending) {
    return (
      <Screen title="Правка группы" back={back} hideHub>
        <Skeleton rows={3} label="Загружаю группу" />
      </Screen>
    )
  }
  if (group.isError) {
    const notFound = (group.error as { kind?: string })?.kind === 'not_found'
    return (
      <Screen title="Правка группы" back={back} hideHub>
        {notFound ? (
          <EmptyState title="Группа не найдена" text="Возможно, её удалили или ссылка устарела." />
        ) : (
          <ErrorState title="Не удалось открыть группу" onRetry={() => void group.refetch()} />
        )}
      </Screen>
    )
  }
  return <GroupEditForm group={group.data} />
}

function GroupEditForm({ group: g }: { group: GroupDetail }) {
  const navigate = useNavigate()
  const { api, toast } = useEnv()
  const invalidate = useInvalidateGroups()
  const flight = useSingleFlight()
  // Исходное - снимок на открытие: перечитанная фоном группа не сбрасывает правку.
  const [initial] = useState(() => ({ name: g.name, description: g.description, coachIds: g.coaches.map((c) => c.id) }))
  const [name, setName] = useState(initial.name)
  const [description, setDescription] = useState(initial.description)
  const [coachIds, setCoachIds] = useState(initial.coachIds)
  const [nameError, setNameError] = useState<string>()
  const [failed, setFailed] = useState(false)

  const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x))
  const dirty = name.trim() !== initial.name || description.trim() !== initial.description.trim() || !sameSet(coachIds, initial.coachIds)
  const leave = useLeaveGuard(dirty)
  const toGroup = () => navigate(`/groups/${g.id}`, { replace: true })
  const back = () => leave.guard(toGroup)
  const slots = useMemo(() => sortSlots(g.schedule), [g.schedule])

  const save = async () => {
    if (!name.trim()) {
      setNameError('Название не может быть пустым')
      return
    }
    setNameError(undefined)
    setFailed(false)
    try {
      await flight.run(async () => {
        const patch: { name?: string; description?: string } = {}
        if (name.trim() !== initial.name) patch.name = name.trim()
        if (description.trim() !== initial.description.trim()) patch.description = description.trim()
        if (patch.name !== undefined || patch.description !== undefined) await api.updateGroup(g.id, patch)
        const coachFailed = await applyCoachDiff((gid, cid, on) => api.setGroupCoach(gid, cid, on), g.id, initial.coachIds, coachIds)
        if (coachFailed > 0) throw new SliceApiError('coach assignment failed', 'network')
        await invalidate(g.id)
        toast('Изменения сохранены')
        toGroup()
      })
    } catch {
      // Всё, что успело сохраниться, повтор отправит ещё раз без вреда.
      setFailed(true)
      void invalidate(g.id)
    }
  }

  return (
    <Screen
      title="Правка группы"
      subtitle={g.name}
      back={back}
      hideHub
      hideNav
      actionBar={
        <>
          <button type="button" className="pc-btn" style={{ flex: 1 }} onClick={back}>
            Отмена
          </button>
          <button type="button" className="pc-btn pc-btn--primary" style={{ flex: 2 }} disabled={flight.busy || !dirty} onClick={() => void save()}>
            {flight.busy ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </>
      }
    >
      <form
        className="pc-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        {failed && <SaveFailedAlert>Изменения остались на экране, попробуйте ещё раз.</SaveFailedAlert>}
        <Field label="Название группы" error={nameError}>
          {(p) => <input {...p} className="pc-input" value={name} maxLength={200} autoComplete="off" onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="Описание" hint={initial.description && !description.trim() ? 'Описание будет стёрто при сохранении.' : 'Можно не заполнять'}>
          {(p) => <textarea {...p} className="pc-input" value={description} onChange={(e) => setDescription(e.target.value)} />}
        </Field>
      </form>

      <CoachPicker selected={coachIds} onChange={setCoachIds} />

      <section className="pc-section" aria-labelledby="pc-edit-schedule">
        <h2 id="pc-edit-schedule" className="pc-section-title">
          Расписание
        </h2>
        {slots.length === 0 ? (
          <p className="pc-muted" style={{ margin: 0, fontSize: 14 }}>
            Расписание не задано.
          </p>
        ) : (
          <ul className="pc-list pc-card" style={{ padding: '0 12px' }} aria-label="Расписание группы">
            {slots.map((s, i) => (
              <li key={i}>
                <div className="pc-kv">
                  <div className="pc-kv__label">{dayName(s.day_of_week)}</div>
                  <div className="pc-kv__value">
                    {formatSlotTime(s)}
                    {s.location ? `, ${s.location}` : ''}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="pc-muted" style={{ margin: '8px 0 0', fontSize: 13 }}>
          Расписание правится отдельно: там видно, что станет с ближайшими занятиями.
        </p>
        <button
          type="button"
          className="pc-btn pc-btn--block"
          style={{ marginTop: 8 }}
          onClick={() => leave.guard(() => navigate(`/groups/${g.id}/schedule`, { replace: true }))}
        >
          Изменить расписание
        </button>
      </section>
      {leave.dialog}
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Удаление группы: точный предпросмотр и явное подтверждение
// ---------------------------------------------------------------------------

/**
 * Что станет при удалении - по тому, как устроен сервер: группа скрывается
 * (мягкое удаление), спортсмены не удаляются и показываются без группы,
 * отметки посещений видны в карточках спортсменов, отметки оплат и записи о
 * группе остаются в базе без экрана, занятия группы пропадают из календаря
 * целиком (и прошедшие). События с группой не связаны - участники поимённо.
 * Без загруженного состава предпросмотр нечестный - удалить нельзя.
 */
export function DeleteGroupSheet({ group: g, onClose }: { group: GroupDetail; onClose: () => void }) {
  const navigate = useNavigate()
  const { api, toast } = useEnv()
  const client = useQueryClient()
  const invalidate = useInvalidateGroups()
  const roster = useGroupAthletes(g.id)
  const flight = useSingleFlight()
  const [failed, setFailed] = useState(false)

  const finish = (text: string) => {
    toast(text)
    navigate('/groups', { replace: true })
    // Карточку удалённой группы не перечитываем - её больше нет.
    client.removeQueries({ queryKey: ['redesign', 'group', g.id] })
    void invalidate()
  }

  const remove = async () => {
    setFailed(false)
    try {
      const ok = await flight.run(() => api.deleteGroup(g.id))
      if (ok === null) return
      finish(`Группа удалена: ${g.name}`)
    } catch (e) {
      // Ответ на первое удаление потерялся, а группа уже скрыта - это успех.
      if (e instanceof SliceApiError && e.kind === 'not_found') finish(`Группа уже удалена: ${g.name}`)
      else setFailed(true)
    }
  }

  return (
    <Sheet title={`Удалить группу «${g.name}»?`} onClose={onClose}>
      {roster.isPending ? (
        <Skeleton rows={2} label="Проверяю состав группы" />
      ) : roster.isError ? (
        <ErrorState
          title="Не удалось проверить состав группы"
          text="Без него не показать, что станет со спортсменами. Удаление недоступно, пока состав не загрузится."
          onRetry={() => void roster.refetch()}
        />
      ) : (
        <>
          <div className="pc-aa-note">
            {roster.data.length === 0 ? (
              <span>В группе нет спортсменов.</span>
            ) : (
              <>
                <span>
                  <b>Спортсмены не удаляются ({roster.data.length}).</b> Они останутся в списке спортсменов без группы,
                  перевести в другую группу можно в карточке спортсмена.
                </span>
                <span className="pc-muted">{roster.data.map((a) => a.name).join(', ')}</span>
              </>
            )}
          </div>
          <div className="pc-aa-note pc-aa-note--danger">
            <b>Пропадёт</b>
            <span>- группа из списка групп вместе с составом, журналом и оплатами группы;</span>
            <span>- занятия группы из «Сегодня» и расписания, и будущие, и прошедшие.</span>
          </div>
          <div className="pc-aa-note">
            <b>Сохранится</b>
            <span>- отметки посещений: видны в карточках спортсменов;</span>
            <span>- отметки оплат и записи о группе: остаются в базе, но открыть их будет негде;</span>
            <span>- тренеры: остаются в справочнике направления;</span>
            <span>- соревнования и сборы: участники записаны поимённо и не меняются.</span>
          </div>
          <p className="pc-muted" style={{ margin: '12px 0 0', fontSize: 14 }}>
            Вернуть группу из приложения нельзя.
          </p>
          {failed && <SaveFailedAlert>Группа не удалена, попробуйте ещё раз.</SaveFailedAlert>}
          <button
            type="button"
            className="pc-btn pc-btn--block pc-btn--danger"
            style={{ marginTop: 12 }}
            disabled={flight.busy}
            onClick={() => void remove()}
          >
            {flight.busy ? 'Удаляю…' : 'Удалить группу'}
          </button>
        </>
      )}
      <button type="button" className="pc-btn pc-btn--block" onClick={onClose}>
        Отмена
      </button>
    </Sheet>
  )
}

